'use client';

import { Box, Flex, Grid, GridItem, StickyBar } from '@coolblue-development/freddy';
import { ProductTitle, SquareProductImage } from '@coolblue-development/next-patterns/client';
import type { Product } from '@features/product/typings';
import { MainProduct } from '@features/second-chance/typings';
import { useBreakpoints } from '@hooks/useBreakpoints';
import { useTranslation } from '@i18n/useTranslation';
import { ReactNode } from 'react';

import RequestLeaseAmount from '../BusinessLeasing/RequestLeaseAmount';
import RequestLeaseButton from '../BusinessLeasing/RequestLeaseButton';
import OrderButton from '../OrderButton';
import { EnergyLabelRenderer } from './EnergyLabelRenderer';

type StickyAddToCartBarProps = {
  product: Product | MainProduct;
  availabilityState: ReactNode;
  propositions?: ReactNode;
  isLoading?: boolean;
  privacyPolicyUrl: string;
  stickyBarSalesPrice: ReactNode;
  shouldDisableAddToCart: boolean;
  isInSinterklaasSplitTest?: boolean;
  isCrossSellOnly?: boolean;
  isSecondChance?: boolean;
  showRequestLeaseButton?: boolean;
  quantity?: number;
  formId?: string;
};

export const StickyAddToCartBar = ({
  product,
  availabilityState,
  propositions,
  isLoading,
  privacyPolicyUrl,
  stickyBarSalesPrice,
  shouldDisableAddToCart,
  isInSinterklaasSplitTest = false,
  isCrossSellOnly = false,
  isSecondChance = false,
  showRequestLeaseButton = false,
  quantity = 1,
  formId
}: StickyAddToCartBarProps) => {
  const { t } = useTranslation('webshop');
  const { isMobile: untilDesktop } = useBreakpoints('lg');
  const { name, primaryImage, energyLabel, subProducts } = product;
  const energyLabelCount =
    subProducts?.filter(subProduct => subProduct?.product?.energyLabel).length || 0;

  return (
    <StickyBar placement='bottom' sx={{ height: 'auto' }}>
      <Flex justifyContent='space-between' columnGap={{ xs: 200, md: 300, lg: 500 }}>
        <Grid justifyContent='space-between' gap={100} sx={{ width: '100%' }}>
          <GridItem
            column={{ md: energyLabelCount > 1 ? 'span 6' : 'span 8', lg: 'span 8' }}
            hideUntil='md'
            alignSelf='center'
          >
            <Grid gap={100}>
              <GridItem column={{ md: 'span 3', lg: 'span 2', xl: 'span 1' }}>
                <SquareProductImage
                  alt='' // As the product name is displayed, the alt attribute is redundant here.
                  imageId={primaryImage.id}
                  size='small'
                  loading='lazy'
                />
              </GridItem>
              <GridItem column={{ md: 'span 9', lg: 'span 10', xl: 'span 11' }} alignSelf='center'>
                <ProductTitle>
                  <ProductTitle.Title>
                    {isSecondChance && t('secondChance.titlePrefix') + ' '}
                    {name}
                  </ProductTitle.Title>
                </ProductTitle>
                {propositions && (
                  <Box pt={100} hideUntil='lg'>
                    {propositions}
                  </Box>
                )}
              </GridItem>
            </Grid>
          </GridItem>
          <GridItem
            column={{
              xs: 'span 12',
              md: energyLabelCount > 1 ? 'span 6' : 'span 4',
              lg: 'span 4'
            }}
            alignSelf={{ md: 'center' }}
            justifySelf={{ md: 'end' }}
          >
            <Flex justifyContent={{ md: 'flex-end' }}>
              {showRequestLeaseButton ? <RequestLeaseAmount /> : stickyBarSalesPrice}
            </Flex>
            {energyLabel !== undefined && (
              <Box mt={{ xs: 200, md: 100 }}>
                <EnergyLabelRenderer
                  energyLabel={energyLabel}
                  subProducts={subProducts}
                  availabilityState={availabilityState}
                />
              </Box>
            )}
          </GridItem>
        </Grid>
        <Flex flexShrink={0} alignSelf='center' columnGap={200}>
          {showRequestLeaseButton ? (
            <RequestLeaseButton product={product as Product} quantity={quantity} />
          ) : (
            <OrderButton
              isLoading={isLoading}
              product={product}
              privacyPolicyUrl={privacyPolicyUrl}
              feature='sticky_header'
              showWishlistButton={!untilDesktop}
              isInSinterklaasSplitTest={isInSinterklaasSplitTest}
              disabled={shouldDisableAddToCart}
              isCrossSellOnly={isCrossSellOnly}
              isSecondChance={isSecondChance}
              formId={formId}
            />
          )}
        </Flex>
      </Flex>
    </StickyBar>
  );
};
